import type { Route } from '@metro/routing'
import type { Mode, SchemaTapGrid } from '../schema/types'
import { hitTest } from '../interaction/tapGridHitTester'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'

export type Theme = 'light' | 'dark'

/** The line the map's line view shows (`/<locale>/lines/<slug>`). */
export interface SelectedLine {
  readonly lineId: string
  /** The URL key (`LineUrlKey.of`) — unique per line even where route names repeat. */
  readonly slug: string
}

/**
 * Where a line view was opened from — the `source` of `line_details_opened`.
 * `url` covers a cold load, a shared link and Google; `map` a tap on a line
 * segment.
 */
export type LineOpenSource = 'url' | 'station_card' | 'station_details' | 'map'

/**
 * Lifecycle of a route-finding request and its results, mirrored from
 * iOS / Android Phase 1.5 Routing state machines. `collapsed` is the
 * idle resting state (no request in flight, no results visible).
 * `loading` covers the in-flight algorithm run. `results` carries the
 * computed routes and which variant the user has selected (default 0), and
 * `departsAt` — the instant (epoch ms) the routes were planned to leave at
 * when that is later than the request (Night Tube mode plans for the next
 * night window); absent means they leave now.
 * `empty` is a terminal "no path found" state. `error` carries a
 * human-readable message for the UI to surface. Independent of
 * `routeSelection` (From/To) — both can transition without affecting
 * the other; the active From/To survives a cleared status, and a
 * cleared From/To does not auto-clear status.
 */
export type RouteStatus =
  | { kind: 'collapsed' }
  | { kind: 'loading' }
  | { kind: 'results'; routes: readonly Route[]; selectedIndex: number; departsAt?: number }
  | { kind: 'empty' }
  | { kind: 'error'; message: string }

export interface MetroMapState {
  theme: Theme
  lightReady: boolean
  darkReady: boolean
  selectedStation: SelectedStationSnapshot | null
  currentMode: Mode
  /** Per-event hint to SelectedStationUrlSync: push history (false) or replace (true). */
  urlReplaceMode: boolean
  /**
   * RouteCard origin/destination selection (web). Independent of
   * `selectedStation` (which represents the *currently inspected* station
   * card). Mirrors iOS `routeSelection` and Android `routeSelection` state
   * — fromStation = origin, toStation = destination. Both null = no
   * route under construction.
   */
  routeSelection: {
    fromStation: SelectedStationSnapshot | null
    toStation: SelectedStationSnapshot | null
    /**
     * Per-session flag (TASK-391 §3.1). `true` once the user has
     * explicitly picked, cleared, or swapped the From slot; `false`
     * while From is still whatever geolocation auto-set (or empty).
     * Distinguishes an auto-set departure station from a user-chosen one
     * so the "my location" button knows whether to silently recenter or
     * also open station details.
     */
    fromWasUserModified: boolean
  }
  /**
   * Lifecycle of the current route-finding request — see [RouteStatus].
   * Independent of `routeSelection` (From/To); a cleared status leaves
   * From/To untouched, and changes to From/To do not auto-mutate this.
   */
  routeStatus: RouteStatus
  /**
   * Line view (`/<locale>/lines/<slug>`): the whole line highlighted like a
   * route, with the line panel. Precedence on the map surface is route
   * (From and To set) > station > line — opening a station from the line
   * view keeps this set so closing the station (or Back) returns to the
   * line; completing a route clears it.
   */
  selectedLine: SelectedLine | null
}

export const initialState: MetroMapState = {
  theme: 'light',
  lightReady: false,
  darkReady: false,
  selectedStation: null,
  currentMode: 'normal',
  urlReplaceMode: false,
  routeSelection: { fromStation: null, toStation: null, fromWasUserModified: false },
  routeStatus: { kind: 'collapsed' },
  selectedLine: null,
}

/**
 * Minimal per-station info the reducer needs at MapTapped time to either
 * build a full [SelectedStationSnapshot] (row 2/5) or emit a row-6
 * analytics event (station id from tapgrid not present in schema.stations).
 */
export interface StationInfo {
  readonly id: string
  readonly localName: string
  readonly canvasX: number
  readonly canvasY: number
  readonly lineId: string
}

/**
 * Minimal station lookup the reducer uses when it needs to resolve a
 * hit-test winner into a SelectedStationSnapshot. The store itself does
 * not own the schema; the caller passes the subset the reducer needs
 * inside the `MapTapped` event. This keeps the reducer independently
 * testable and the store free of network concerns.
 */
export interface StationLookup {
  readonly tapGrids: readonly SchemaTapGrid[]
  readonly stationsById: ReadonlyMap<string, StationInfo>
}

export type MetroMapEvent =
  | { kind: 'SystemThemeChanged'; theme: Theme }
  | { kind: 'GeometryReady'; theme: Theme }
  | {
      kind: 'SetMode'
      mode: Mode
      /**
       * TASK-412 M / Q: night-service station ids. Supplied by the
       * dispatcher (from `nightElementIds(schema)`) so the PURE reducer can
       * clear selections that aren't on the Night Tube when mode flips to
       * `'night'` — a non-night From/To/selected station made in normal
       * mode must not survive into night (the user could otherwise build a
       * route to a station with no night service). Omitted → no clearing
       * (e.g. flipping back to normal, or callers with no schema yet).
       */
      nightStationIds?: ReadonlySet<string>
      /**
       * Whether the open line view's line has any night-service station.
       * Supplied by the dispatcher alongside `nightStationIds`; `false`
       * clears the line view on entering night (nothing of it would be
       * drawn). Omitted → the line view is left alone.
       */
      lineHasNightService?: boolean
    }
  | {
      kind: 'MapTapped'
      canvasX: number
      canvasY: number
      stationsByTapGrid: StationLookup
      /**
       * Night-service allow-set (station ids drawn on the Night Tube).
       * Supplied by the dispatcher (from `nightElementIds(schema)`) ONLY in
       * night mode; omitted in normal mode. When present, a hit-test winner
       * that is NOT in this set is a station HIDDEN by the night filter —
       * the reducer rejects it (treats the tap as empty area) so the user
       * cannot select an invisible station and build a route that returns
       * "No routes found". The tapGrid mode filter and the drawn night
       * geometry can drift (tapGrid `modes` vs station `modes`); this is the
       * authoritative cross-check against what is actually drawn. Day mode
       * unchanged (absent → no rejection). Mirrors iOS/Android night hit
       * rejection.
       */
      nightStationIds?: ReadonlySet<string>
      /**
       * §13 (TASK-412): set by the canvas when this map tap is also
       * dismissing the open Suggester side panel. It BYPASSES the
       * route-active block below so a station tap still opens
       * StationDetails (and an empty tap still clears the selection)
       * even while a route is built (`routeStatus !== collapsed`) — the
       * suggester can be reopened over a results panel. Matches Android
       * `handleMapTapped` (no route-active guard on the dismiss path).
       * Absent / false → the normal route-active block applies (the
       * pre-existing TASK-275 §4.8 "don't hijack route slots" behaviour).
       */
      dismissSuggester?: boolean
      /**
       * The line whose segment lies under the tap, resolved by the canvas
       * (`createLineSegmentHitTester`) for lines that have a line view.
       * Used only when no station is hit: a station tap always wins.
       */
      segmentLine?: SelectedLine
    }
  /**
   * Direct selection from outside a tap — used by the router layer when a
   * user lands on `/stations/<slug>` (from a shared link, Google SERP,
   * bookmark, etc.) without ever tapping the map. Bypasses hit-testing;
   * caller passes the pre-resolved snapshot built from schema lookup. Set
   * `snapshot` to `null` to clear the selection (e.g., on panel unmount).
   */
  | { kind: 'StationSelected'; snapshot: SelectedStationSnapshot | null; urlReplaceMode?: boolean }
  /**
   * Emitted by the StationDetails carousel after the user swipes between
   * cards and the new card has settled (post-snap). Always rewrites the
   * URL via `urlReplaceMode=true` so back-button skips the carousel pages
   * and returns to the previous map view in one step. Equivalent to
   * iOS / Android's carousel-settle reducer event.
   */
  | { kind: 'CarouselSettled'; snapshot: SelectedStationSnapshot }
  /**
   * RouteCard: set the origin (From) station. Idempotent on id — a
   * repeat of the same station is a no-op (preserves state ref).
   */
  | { kind: 'RouteFromSelected'; station: SelectedStationSnapshot }
  /**
   * TASK-391: set the origin (From) station from geolocation (launch
   * snap or "my location" button). Unlike `RouteFromSelected` this does
   * NOT flip `fromWasUserModified` — an auto-set departure stays
   * "not user-modified". Idempotent on id (preserves state ref).
   */
  | { kind: 'RouteFromSelectedByGeolocation'; station: SelectedStationSnapshot }
  /** RouteCard: clear the origin (From) station. Idempotent. */
  | { kind: 'RouteFromCleared' }
  /**
   * RouteCard: set the destination (To) station. Idempotent on id — a
   * repeat of the same station is a no-op (preserves state ref).
   */
  | { kind: 'RouteToSelected'; station: SelectedStationSnapshot }
  /** RouteCard: clear the destination (To) station. Idempotent. */
  | { kind: 'RouteToCleared' }
  /**
   * RouteCard: swap From and To slots. No caller dispatches this in
   * the current scope; reducer handles it for forward compatibility
   * (matches iOS / Android swap behaviour). No-op when both null.
   */
  | { kind: 'RouteSelectionSwapped' }
  /**
   * Routing lifecycle: a route-finding request has started.
   * Transitions `routeStatus` to `loading`. No-op when already loading
   * (preserves state ref).
   */
  | { kind: 'RouteRequestStarted' }
  /**
   * Routing lifecycle: route results have arrived. Empty array →
   * `empty`. Non-empty → `results` with `selectedIndex` clamped to
   * `[0, routes.length-1]` (defaults to 0 when omitted). The optional
   * `selectedIndex` exists for Task 22 persistence restoration so the
   * UI can re-hydrate a previously-chosen variant after page reload.
   */
  | {
      kind: 'RouteResultsReceived'
      routes: readonly Route[]
      selectedIndex?: number
      /** When the routes were planned to leave, if later than now (`RouteStatus.results`). */
      departsAt?: number
    }
  /**
   * Routing lifecycle: the route-finding request failed. Transitions
   * `routeStatus` to `error` with a human-readable message.
   */
  | { kind: 'RouteRequestFailed'; message: string }
  /**
   * Routing lifecycle: user picked a different variant from the
   * results list. Only valid when `routeStatus.kind === 'results'`;
   * otherwise a no-op. Index is clamped to `[0, routes.length-1]`.
   * Same-index dispatch is a no-op (preserves state ref).
   */
  | { kind: 'RouteSelectedVariantChanged'; index: number }
  /**
   * Routing lifecycle: dismiss the routing UI back to its idle state.
   * Resets `routeStatus` to `collapsed`. Does NOT touch `routeSelection`
   * — From/To survive (matches iOS / Android). No-op when already
   * collapsed.
   */
  | { kind: 'RouteCleared' }
  /**
   * Open the line view: from the URL (`LineUrlSync`), the line link on a
   * station card / StationDetails header, or a segment tap. Closes any open
   * station. Idempotent on the line id (no state change, no analytics).
   */
  | { kind: 'LineSelected'; line: SelectedLine; source: LineOpenSource }
  /** Close the line view (close button, Esc, empty map tap). Idempotent. */
  | { kind: 'LineCleared' }

export type Effect =
  | { kind: 'LogTap'; message: string }
  | { kind: 'ReportError'; event: string; metadata: Record<string, unknown> }
  /**
   * Product analytics event (catalog G-1 map-surface instrumentation).
   * Distinct from `ReportError` — which is the diagnostic schema-drift sink
   * (`reportError` on native) — this carries a first-class behaviour event
   * (`station_tapped`, …) that the effect runner forwards to the project
   * `MetroAnalyticsReporter` (GA4 + Amplitude). Params are
   * already in the cross-platform snake_case wire shape; the runner forwards
   * verbatim and `reportAnalyticsEvent` applies OMIT-when-null.
   */
  | { kind: 'ReportEvent'; event: string; params: Record<string, unknown> }

export interface ReducerResult {
  state: MetroMapState
  effects: readonly Effect[]
}

/**
 * Route ⇄ station mutual-exclusivity rule (bug-fix 2026-07-06).
 *
 * A BUILT route (both From and To set) and an inspected station-detail are
 * mutually exclusive on web. The moment the From/To pair becomes COMPLETE
 * (both non-null), any `selectedStation` is superseded and cleared so the
 * app leaves the `/stations/<slug>` surface and renders ONLY the route
 * (RouteCard + RouteDetails). This is the single source of truth for the
 * "route supersedes inspected station" transition — the From/To selection
 * events funnel their resulting pair through here.
 *
 * Idempotent: while the pair is incomplete (either slot null) the current
 * `selectedStation` is returned untouched, so a partial route build (only
 * From, or only To) does not disturb an open station card.
 *
 * Deliberately NOT applied on `MapTapped`: §13 (TASK-412) still allows a
 * deliberate map station-tap to open StationDetails over a built route.
 */
/** The line-view half of the same rule: a complete From/To pair closes the line view. */
function lineClearedWhenRouteComplete(
  state: MetroMapState,
  from: SelectedStationSnapshot | null,
  to: SelectedStationSnapshot | null,
): SelectedLine | null {
  return from !== null && to !== null ? null : state.selectedLine
}

function stationClearedWhenRouteComplete(
  state: MetroMapState,
  from: SelectedStationSnapshot | null,
  to: SelectedStationSnapshot | null,
): SelectedStationSnapshot | null {
  return from !== null && to !== null ? null : state.selectedStation
}

export function metroMapReducer(state: MetroMapState, event: MetroMapEvent): ReducerResult {
  switch (event.kind) {
    case 'SystemThemeChanged': {
      if (state.theme === event.theme) return { state, effects: [] }
      return { state: { ...state, theme: event.theme }, effects: [] }
    }
    case 'GeometryReady': {
      if (event.theme === 'light') {
        if (state.lightReady) return { state, effects: [] }
        return { state: { ...state, lightReady: true }, effects: [] }
      }
      if (state.darkReady) return { state, effects: [] }
      return { state: { ...state, darkReady: true }, effects: [] }
    }
    case 'SetMode': {
      if (state.currentMode === event.mode) return { state, effects: [] }
      // TASK-412 Q (upgrades P3): entering night mode CLEARS — not just
      // hides — any state pinned to a station with NO night service, so the
      // user can't route to / inspect an off-night-tube station. A
      // non-night From/To resets to empty; a non-night selectedStation
      // (open StationDetails / card / bare map-tap focus) is dismissed.
      // Cleared selections are NOT auto-restored on toggle-off (mirror iOS
      // Q @final). Only applied when entering night WITH the night id set;
      // leaving night, or no set supplied, touches nothing.
      const enteringNight = event.mode === 'night' && state.currentMode !== 'night'
      const nightIds = event.nightStationIds
      // A line view whose line has no night service has nothing left to
      // draw in night mode — close it the same way.
      const selectedLine =
        enteringNight && event.lineHasNightService === false ? null : state.selectedLine
      if (!enteringNight || nightIds === undefined) {
        return { state: { ...state, currentMode: event.mode, selectedLine }, effects: [] }
      }
      const isNight = (s: SelectedStationSnapshot | null): boolean =>
        s === null || nightIds.has(s.id)
      const from = isNight(state.routeSelection.fromStation)
        ? state.routeSelection.fromStation
        : null
      const to = isNight(state.routeSelection.toStation) ? state.routeSelection.toStation : null
      const selected = isNight(state.selectedStation) ? state.selectedStation : null
      return {
        state: {
          ...state,
          currentMode: event.mode,
          selectedStation: selected,
          selectedLine,
          routeSelection: {
            ...state.routeSelection,
            fromStation: from,
            toStation: to,
          },
        },
        effects: [],
      }
    }
    case 'MapTapped': {
      // Block map taps while a route is active (loading / results / empty
      // / error). The user is reviewing routes — opening a station card
      // would steal the bottom slot from RouteCard and reset the dim.
      // To pick a different From / To, the user must clear the existing
      // selection via the X buttons on the destination chips first.
      //
      // §13 EXCEPTION: a tap that dismisses the open Suggester side panel
      // (`dismissSuggester`) bypasses this block — the suggester can be
      // reopened over a built route, and §13 requires the station tap to
      // still open StationDetails (empty tap to still clear) while
      // route-building. Parity with Android `handleMapTapped`.
      if (event.dismissSuggester !== true && state.routeStatus.kind !== 'collapsed')
        return { state, effects: [] }
      const raw = hitTest(
        event.canvasX,
        event.canvasY,
        state.currentMode,
        event.stationsByTapGrid.tapGrids,
      )
      // Server schema bug workaround: `tapGrids[].stationId` carries the
      // XML numeric entity `&#38;` where `stations[].id` has raw `&`
      // (Hammersmith & City — `h&c` vs `h&#38;c`). Canonicalize to the
      // raw form so state is consistent and localName lookups hit.
      // Keep in sync with iOS `MetroMapSystem.swift` and Android
      // `MetroMapReducer.kt` — load-bearing cross-platform.
      const canonical = raw === null ? null : raw.replace(/&#38;/g, '&')
      // Night hit rejection: in night mode (`nightStationIds` supplied) a
      // hit on a station NOT in the drawn night set is a station hidden by
      // the night filter. Coerce it to `null` so it flows through the
      // empty-area path — the user must not select an invisible station and
      // build an invalid "No routes found" route. Day mode: `nightStationIds`
      // is absent, so `candidate === canonical` and behaviour is unchanged.
      const candidate =
        canonical !== null &&
        event.nightStationIds !== undefined &&
        !event.nightStationIds.has(canonical)
          ? null
          : canonical
      // No-op: same station already selected (or still no selection).
      // `state.selectedStation?.id` is undefined when nothing is selected;
      // compare null vs null explicitly so row 1 (tap empty, nothing selected)
      // short-circuits correctly.
      // Empty-area tap with no station open: a line segment under the tap
      // opens that line's view; otherwise the tap closes an open line view.
      if (candidate === null && state.selectedStation === null) {
        if (event.segmentLine !== undefined) {
          return openLine(state, event.segmentLine, 'map')
        }
        if (state.selectedLine !== null) {
          return { state: { ...state, selectedLine: null }, effects: [] }
        }
        return { state, effects: [] }
      }
      if (candidate !== null && candidate === state.selectedStation?.id)
        return { state, effects: [] }
      if (candidate === null) {
        return {
          state: { ...state, selectedStation: null },
          effects: [{ kind: 'LogTap', message: '[MetroMap] tap → empty area' }],
        }
      }
      const stationInfo = event.stationsByTapGrid.stationsById.get(candidate)
      if (stationInfo === undefined) {
        // Row 6: schema drift. State unchanged, log + analytics.
        return {
          state,
          effects: [
            {
              kind: 'LogTap',
              message: `[MetroMap] tap → ${candidate}: station not in mapData, ignoring`,
            },
            {
              kind: 'ReportError',
              event: 'metro_map.select.station_not_found',
              metadata: { stationId: candidate },
            },
          ],
        }
      }
      const snapshot: SelectedStationSnapshot = {
        id: stationInfo.id,
        canvasX: stationInfo.canvasX,
        canvasY: stationInfo.canvasY,
        lineId: stationInfo.lineId,
        localName: stationInfo.localName,
      }
      return {
        state: { ...state, selectedStation: snapshot },
        effects: [
          { kind: 'LogTap', message: `[MetroMap] tap → ${candidate}: ${stationInfo.localName}` },
          // G-1: first-class map-surface behaviour event. `source: 'map'`
          // — the tap originated from a station node on the map (vs.
          // `search` / `deeplink`, which other call sites own). Byte-
          // identical with iOS / Android `station_tapped`.
          {
            kind: 'ReportEvent',
            event: 'station_tapped',
            params: { station_id: snapshot.id, source: 'map' },
          },
        ],
      }
    }
    case 'StationSelected': {
      // Idempotent on identity: if we're already selecting this station
      // (or already null), don't re-emit state changes. Protects the
      // tap ↔ URL loop from bouncing: tap dispatches MapTapped → store
      // updates → AppShell navigate() → StationPanel mounts → dispatches
      // StationSelected with the same snapshot → no-op here.
      const nextId = event.snapshot?.id ?? null
      const prevId = state.selectedStation?.id ?? null
      // For snapshot=null (deselect) replaceMode is false; for snapshot
      // non-null, use the flag (default false). The `urlReplaceMode`
      // hint is consumed by SelectedStationUrlSync to choose between
      // push (history) and replace (rewrite current entry).
      const nextReplaceMode = event.snapshot !== null && (event.urlReplaceMode ?? false)
      if (nextId === prevId && state.urlReplaceMode === nextReplaceMode) {
        return { state, effects: [] }
      }
      if (nextId === prevId) {
        // Same selection identity but replaceMode flag differs (e.g.,
        // deselect after a replace-mode select needs to clear the flag).
        return {
          state: { ...state, urlReplaceMode: nextReplaceMode },
          effects: [],
        }
      }
      const msg = event.snapshot
        ? `[MetroMap] select → ${event.snapshot.id}: ${event.snapshot.localName}`
        : '[MetroMap] deselect'
      return {
        state: { ...state, selectedStation: event.snapshot, urlReplaceMode: nextReplaceMode },
        effects: [{ kind: 'LogTap', message: msg }],
      }
    }
    case 'CarouselSettled': {
      // No-op when same id; preserves referential equality so observers
      // don't re-render unnecessarily. Without this guard, a settle echo
      // (e.g., StationDetails reporting the already-selected card after
      // mount) would still write to history even though nothing changed.
      if (state.selectedStation?.id === event.snapshot.id) {
        return { state, effects: [] }
      }
      return {
        state: { ...state, selectedStation: event.snapshot, urlReplaceMode: true },
        effects: [],
      }
    }
    case 'RouteFromSelected': {
      const current = state.routeSelection.fromStation
      if (current !== null && current.id === event.station.id) return { state, effects: [] }
      // Cluster-collision rule: if the OPPOSITE slot (To) is a sibling
      // of the new From (same `naptanId`), clear it. Otherwise the pair
      // collapses to `sameOriginDestination` once Routing's signature
      // dedup runs through naptanId — leaving the user staring at "no
      // routes found" until they manually clear the other slot.
      const otherTo = state.routeSelection.toStation
      const naptan = event.station.naptanId
      const collidesWithTo = naptan !== undefined && otherTo !== null && otherTo.naptanId === naptan
      const nextTo = collidesWithTo ? null : otherTo
      return {
        state: {
          ...state,
          // A completed From+To pair supersedes any inspected station.
          selectedStation: stationClearedWhenRouteComplete(state, event.station, nextTo),
          selectedLine: lineClearedWhenRouteComplete(state, event.station, nextTo),
          routeSelection: {
            ...state.routeSelection,
            fromStation: event.station,
            toStation: nextTo,
            fromWasUserModified: true,
          },
        },
        effects: [],
      }
    }
    case 'RouteFromSelectedByGeolocation': {
      const current = state.routeSelection.fromStation
      if (current !== null && current.id === event.station.id) return { state, effects: [] }
      // Same cluster-collision rule as `RouteFromSelected`, but the
      // `fromWasUserModified` flag is intentionally NOT set — this is an
      // auto-set departure (launch snap / "my location" button), not a
      // user choice.
      const otherTo = state.routeSelection.toStation
      const naptan = event.station.naptanId
      const collidesWithTo = naptan !== undefined && otherTo !== null && otherTo.naptanId === naptan
      const nextTo = collidesWithTo ? null : otherTo
      return {
        state: {
          ...state,
          // A completed From+To pair supersedes any inspected station.
          selectedStation: stationClearedWhenRouteComplete(state, event.station, nextTo),
          selectedLine: lineClearedWhenRouteComplete(state, event.station, nextTo),
          routeSelection: {
            ...state.routeSelection,
            fromStation: event.station,
            toStation: nextTo,
          },
        },
        effects: [],
      }
    }
    case 'RouteFromCleared': {
      // Clearing an already-empty From is a no-op — bail with the same
      // state ref. Previously the bailout also required
      // `fromWasUserModified`, so a spurious `RouteFromCleared` on a
      // fresh store flipped the flag to `true` and defeated the §3.2
      // launch-snap "not user-modified" contract.
      if (state.routeSelection.fromStation === null) {
        return { state, effects: [] }
      }
      return {
        state: {
          ...state,
          routeSelection: {
            ...state.routeSelection,
            fromStation: null,
            fromWasUserModified: true,
          },
        },
        effects: [],
      }
    }
    case 'RouteToSelected': {
      const current = state.routeSelection.toStation
      if (current !== null && current.id === event.station.id) return { state, effects: [] }
      // Symmetric cluster-collision rule (see `RouteFromSelected`):
      // clear the From slot if the new To is a sibling of it.
      const otherFrom = state.routeSelection.fromStation
      const naptan = event.station.naptanId
      const collidesWithFrom =
        naptan !== undefined && otherFrom !== null && otherFrom.naptanId === naptan
      const nextFrom = collidesWithFrom ? null : otherFrom
      return {
        state: {
          ...state,
          // A completed From+To pair supersedes any inspected station.
          selectedStation: stationClearedWhenRouteComplete(state, nextFrom, event.station),
          selectedLine: lineClearedWhenRouteComplete(state, nextFrom, event.station),
          routeSelection: {
            ...state.routeSelection,
            fromStation: nextFrom,
            toStation: event.station,
          },
        },
        effects: [],
      }
    }
    case 'RouteToCleared': {
      if (state.routeSelection.toStation === null) return { state, effects: [] }
      return {
        state: {
          ...state,
          routeSelection: { ...state.routeSelection, toStation: null },
        },
        effects: [],
      }
    }
    case 'RouteSelectionSwapped': {
      const { fromStation, toStation } = state.routeSelection
      if (fromStation === null && toStation === null) return { state, effects: [] }
      return {
        state: {
          ...state,
          // Swap can only leave a COMPLETE pair complete (both were set) —
          // in that case the built route supersedes any inspected station.
          selectedStation: stationClearedWhenRouteComplete(state, toStation, fromStation),
          selectedLine: lineClearedWhenRouteComplete(state, toStation, fromStation),
          routeSelection: {
            fromStation: toStation,
            toStation: fromStation,
            fromWasUserModified: true,
          },
        },
        effects: [],
      }
    }
    case 'RouteRequestStarted': {
      if (state.routeStatus.kind === 'loading') return { state, effects: [] }
      return {
        state: { ...state, routeStatus: { kind: 'loading' } },
        effects: [],
      }
    }
    case 'RouteResultsReceived': {
      if (event.routes.length === 0) {
        return {
          state: { ...state, routeStatus: { kind: 'empty' } },
          effects: [],
        }
      }
      const idx =
        event.selectedIndex !== undefined
          ? Math.max(0, Math.min(event.routes.length - 1, event.selectedIndex))
          : 0
      return {
        state: {
          ...state,
          routeStatus: {
            kind: 'results',
            routes: event.routes,
            selectedIndex: idx,
            ...(event.departsAt === undefined ? {} : { departsAt: event.departsAt }),
          },
        },
        effects: [],
      }
    }
    case 'RouteRequestFailed': {
      return {
        state: { ...state, routeStatus: { kind: 'error', message: event.message } },
        effects: [],
      }
    }
    case 'RouteSelectedVariantChanged': {
      if (state.routeStatus.kind !== 'results') return { state, effects: [] }
      const { routes, selectedIndex } = state.routeStatus
      const clamped = Math.max(0, Math.min(routes.length - 1, event.index))
      if (clamped === selectedIndex) return { state, effects: [] }
      return {
        state: {
          ...state,
          routeStatus: { ...state.routeStatus, selectedIndex: clamped },
        },
        effects: [],
      }
    }
    case 'RouteCleared': {
      if (state.routeStatus.kind === 'collapsed') return { state, effects: [] }
      return {
        state: { ...state, routeStatus: { kind: 'collapsed' } },
        effects: [],
      }
    }
    case 'LineSelected':
      return openLine(state, event.line, event.source)
    case 'LineCleared': {
      if (state.selectedLine === null) return { state, effects: [] }
      return { state: { ...state, selectedLine: null }, effects: [] }
    }
  }
}

/**
 * Opens the line view on `line`, closing any open station (the station
 * card's line link and a segment tap both leave the station for the line).
 * The line already selected is not reported again — Back from a station
 * opened out of the line view lands here through the URL sync, and only
 * the station has to go.
 */
function openLine(state: MetroMapState, line: SelectedLine, source: LineOpenSource): ReducerResult {
  if (state.selectedLine?.lineId === line.lineId) {
    if (state.selectedStation === null) return { state, effects: [] }
    return { state: { ...state, selectedStation: null, urlReplaceMode: false }, effects: [] }
  }
  return {
    state: { ...state, selectedLine: line, selectedStation: null, urlReplaceMode: false },
    effects: [
      {
        kind: 'ReportEvent',
        event: 'line_details_opened',
        params: { line_id: line.lineId, source },
      },
    ],
  }
}

// --- store ---

export interface Store<S, E> {
  getState: () => S
  dispatch: (event: E) => readonly Effect[]
  subscribe: (listener: () => void) => () => void
}

export function createStore(
  reducer: (state: MetroMapState, event: MetroMapEvent) => ReducerResult,
  initial: MetroMapState = initialState,
): Store<MetroMapState, MetroMapEvent> {
  let state = initial
  const listeners = new Set<() => void>()
  return {
    getState: () => state,
    dispatch: (event) => {
      const next = reducer(state, event)
      if (next.state !== state) {
        state = next.state
        for (const l of listeners) l()
      }
      return next.effects
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
